// Eskom PARCC — full-population Excel export ("Download all transactions").
//
// A one-click download of the whole (live) population as an XLSX that mirrors the
// bulk-upload template, populated with every current field value INCLUDING the
// review updates. Three sheets:
//   • Transactions  — one row per live transaction: a population block (bulk-upload
//                     columns, so the left side re-imports cleanly) + an updates
//                     block (review work-product, incl. the DERIVED effective
//                     compliance the dashboard shows).
//   • Findings      — one row per individual finding line (reuses the findings
//                     register column model), scoped to the exported transactions.
//   • CES Checklist — one row per CES evaluation row.
//
// Pure string[][] builders — unit-tested as data. The XLSX/Blob glue (workbook.ts
// + download.ts) is separate. Every cell passes through neutraliseCell so a value
// a spreadsheet could read as a formula can't execute (defence-in-depth).
import type { Transaction } from '../../data/types';
import type { FindingRead, CesRowRead } from '../../lib/db';
import { neutraliseCell } from './neutraliseCell';
import { buildFindingsSheet } from './buildFindingsSheet';
import type { ComplianceSheet } from './buildComplianceWorkbook';
import { effectiveComplianceByRef } from '../transactions/compliance';
import { genuineFindings } from '../../domain/findings';

/** Context a transaction column may read beyond the row itself. */
interface ColumnContext {
  /** referenceNo → derived effective compliance level (the register/dashboard value). */
  complianceByRef: ReadonlyMap<string, string>;
}

/** A boolean rendered for the sheet: Y / N, or blank when unset. */
function yn(value: boolean | undefined): string {
  if (value === true) return 'Y';
  if (value === false) return 'N';
  return '';
}

/** A numeric value rendered for the sheet (round-trips via the importer's parseRand). */
function numeric(value: number | undefined): string {
  return value == null || Number.isNaN(value) ? '' : String(value);
}

/**
 * The ordered Transaction-sheet columns — a single source of truth pairing each
 * header with its accessor, so the header row and the cell rows can never drift.
 * Population block first (bulk-upload headers, re-import-friendly), then the
 * review-updates block. The updates headers are non-import-alias names so a
 * re-import ignores them — with ONE benign exception: `Reviewed` normalises to
 * the importer's `Reviewed Y/N` alias, but `rowToImport` never consumes that
 * field (it's header-scoring only), so a re-import still can't flip review state.
 */
export const TRANSACTION_EXPORT_COLUMNS: readonly {
  readonly header: string;
  readonly cell: (tx: Transaction, ctx: ColumnContext) => string;
}[] = [
  // --- Population block (mirrors the bulk-upload template) ---
  { header: 'Dataset', cell: (t) => String(t.dataset ?? '') },
  { header: 'Reference No', cell: (t) => t.referenceNo ?? '' },
  { header: 'Year', cell: (t) => t.year ?? '' },
  { header: 'Period', cell: (t) => t.period ?? '' },
  { header: 'Division', cell: (t) => t.division ?? '' },
  { header: 'OU', cell: (t) => t.ou ?? '' },
  { header: 'BU', cell: (t) => t.bu ?? '' },
  { header: 'Contract Number', cell: (t) => t.contractNumber ?? '' },
  { header: 'Mechanism', cell: (t) => t.mechanism ?? '' },
  { header: 'Panel Release', cell: (t) => yn(t.panel) },
  { header: 'Supplier', cell: (t) => t.supplier ?? '' },
  { header: 'CSD Number', cell: (t) => t.csdNumber ?? '' },
  { header: 'Brief Description', cell: (t) => t.briefDescription ?? '' },
  { header: 'Buyer', cell: (t) => t.buyer ?? '' },
  { header: 'PR Number', cell: (t) => t.prNumber ?? '' },
  { header: 'Procurement Manager', cell: (t) => t.procurementManager ?? '' },
  { header: 'End-User', cell: (t) => t.endUser ?? '' },
  { header: 'Value', cell: (t) => numeric(t.value) },
  { header: 'Value (Display)', cell: (t) => t.valueDisplay ?? '' },
  { header: 'Self-declaration Listing', cell: (t) => t.selfDeclaration ?? '' },
  { header: 'Gaps Status', cell: (t) => t.gapsStatus ?? '' },
  { header: 'Compliance Level', cell: (t) => t.complianceLevel ?? '' },
  { header: 'Initial Findings', cell: (t) => t.initialFindings ?? '' },
  { header: 'Updated Findings', cell: (t) => t.updatedFindings ?? '' },
  { header: 'Link', cell: (t) => t.link ?? '' },
  { header: 'Link Provided', cell: (t) => yn(t.linkProvided) },
  { header: 'Link Issue', cell: (t) => t.linkIssue ?? '' },
  { header: 'CIR Number', cell: (t) => t.cirNumber ?? '' },
  // --- Updates block (the review work-product) ---
  { header: 'Effective Compliance', cell: (t, ctx) => ctx.complianceByRef.get(t.referenceNo) ?? '' },
  { header: 'Compliance Override', cell: (t) => t.complianceLevelOverride ?? '' },
  { header: 'Finding Text', cell: (t) => t.findingText ?? '' },
  { header: 'Working Paper Notes', cell: (t) => t.workingPaperNotes ?? '' },
  { header: 'Progress', cell: (t) => t.progress ?? '' },
  { header: 'Reviewed', cell: (t) => yn(t.reviewed) },
  { header: 'Review Status', cell: (t) => t.reviewStatus ?? '' },
  { header: 'Reviewer', cell: (t) => t.reviewerEmail ?? '' },
  { header: 'Reviewed At', cell: (t) => t.reviewedAt ?? '' },
  { header: 'Assigned To', cell: (t) => t.assignedToEmail ?? '' },
  { header: 'Captured By', cell: (t) => t.capturedByEmail ?? '' },
  { header: 'Captured Date', cell: (t) => t.capturedDate ?? '' },
  { header: 'On Dashboard', cell: (t) => yn(t.onDashboard) },
];

/** The Transaction-sheet header row, derived from the column model. */
export const TRANSACTION_EXPORT_HEADERS: readonly string[] = TRANSACTION_EXPORT_COLUMNS.map(
  (c) => c.header,
);

/** The CES-detail sheet columns. */
export const CES_DETAIL_HEADERS = [
  'Reference No',
  'Seq',
  'Stage',
  'Criterion',
  'Status',
  'Finding / Evidence',
  'Anchor',
] as const;

/**
 * Build the Transactions sheet (header row + one row per transaction). Pass the
 * transactions to export (already scoped + live by the caller) and the
 * referenceNo → effective-compliance map. Every cell is neutralised.
 */
export function buildTransactionsSheet(
  transactions: readonly Transaction[],
  complianceByRef: ReadonlyMap<string, string>,
): string[][] {
  const ctx: ColumnContext = { complianceByRef };
  const header = [...TRANSACTION_EXPORT_HEADERS];
  const rows = transactions.map((tx) =>
    TRANSACTION_EXPORT_COLUMNS.map((col) => neutraliseCell(col.cell(tx, ctx))),
  );
  return [header, ...rows];
}

/**
 * Build the CES-checklist sheet (header row + one row per CES row) from the
 * `referenceNo → CesRowRead[]` map. When `refs` is supplied, only rows for those
 * references are emitted (used to scope the sheet to the exported transactions).
 */
export function buildCesDetailSheet(
  cesByRef: ReadonlyMap<string, readonly CesRowRead[]>,
  refs?: ReadonlySet<string>,
): string[][] {
  const header = [...CES_DETAIL_HEADERS];
  const rows: string[][] = [];
  for (const [ref, list] of cesByRef) {
    if (refs && !refs.has(ref)) continue;
    for (const r of list) {
      rows.push(
        [ref, String(r.seq), r.stage, r.criterion, r.status, r.finding, r.anchor ?? ''].map(
          neutraliseCell,
        ),
      );
    }
  }
  return [header, ...rows];
}

/**
 * Assemble the full-population export: the three named sheets, ready for
 * `workbookFromSheets`. Soft-deleted transactions are dropped; findings and CES
 * rows are scoped to the surviving references (and non-deleted findings only).
 * The effective compliance shown on the Transactions sheet is the DERIVED
 * roll-up (`effectiveComplianceByRef`) — the same value the register/dashboard
 * show — not the raw stored column.
 */
export function buildAllTransactionsSheets(
  transactions: readonly Transaction[],
  findings: readonly FindingRead[],
  cesByRef: ReadonlyMap<string, readonly CesRowRead[]>,
): ComplianceSheet[] {
  const live = transactions.filter((t) => !t.deleted);
  const refs = new Set(live.map((t) => t.referenceNo));
  const txByRef = new Map(live.map((t) => [t.referenceNo, t]));
  const complianceByRef = effectiveComplianceByRef(live, findings);
  const liveFindings = findings.filter(
    (f) => !f.deleted && !!f.referenceNo && refs.has(f.referenceNo),
  );
  // The Findings sheet counts what the register counts: one genuine finding per
  // (transaction × criterion), no "Additional comments" notes, no duplicates
  // (owner feedback Aug 2026 — LPO 4300497475 read 40 raw rows, 26 genuine).
  return [
    { sheetName: 'Transactions', rows: buildTransactionsSheet(live, complianceByRef) },
    { sheetName: 'Findings', rows: buildFindingsSheet(genuineFindings(liveFindings), txByRef) },
    { sheetName: 'CES Checklist', rows: buildCesDetailSheet(cesByRef, refs) },
  ];
}
